import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UpdateIcon from '@mui/icons-material/Update'
import CustomDataGrid from '../../../../../components/DataGrid/CustomDataGrid'
import API from '../../../../../services/API'
import BigModal from '../../../../../components/Modals/BigModal'
import AssignResource from '../AssignResource'
import { ControlRoomContext } from '../ControlPage'
import ResourceTimesPopup from './ResourceTimesPopup'

const fieldOrder = [
  'assigned',
  'mobile',
  'arriveScene',
  'leaveScene',
  'arriveHospital',
  'patientHandover',
  'clear',
]

const TimeCell = memo(
  ({
    value,
    row,
    field,
    newStatus,
    selectedIncident,
    selectedControlRoom,
    setSelectedIncident,
    setOpenResourceTimes,
    updatingTimeKey,
    setUpdatingTimeKey,
    canDispatch = true,
  }) => {
    const timeKey = `${row._id}-${field}`
    const updating = updatingTimeKey === timeKey

    const openTimesPopup = (e) => {
      e.stopPropagation()
      if (!canDispatch) return
      setOpenResourceTimes({ ...row })
    }

    const handleUpdateTime = async (e) => {
      e.stopPropagation()

      if (updating) return

      const now = new Date().toISOString()

      setUpdatingTimeKey(timeKey)

      try {
        const res = await API.post('/commandcontrol/updateresourcetimes', {
          incidentID: selectedIncident.id,
          assignedResourceRowID: row._id,
          field,
          controlRoomID: selectedControlRoom,
          resourceID: row.resourceId,
          newStatus,
          newDate: now,
        })

        if (res.status === 200) {
          setSelectedIncident((prev) => {
            if (!prev) return prev

            return {
              ...prev,
              assignedResources: (prev.assignedResources ?? []).map((r) =>
                r._id === row._id
                  ? {
                      ...r,
                      times: {
                        ...r.times,
                        [field]: now,
                      },
                    }
                  : r,
              ),
            }
          })
        }
      } finally {
        setUpdatingTimeKey(null)
      }
    }

    const currentIndex = fieldOrder.indexOf(field)
    const laterFields = fieldOrder.slice(currentIndex + 1)
    const laterHasValue = laterFields.some((f) => row.times?.[f])

    if (!value && laterHasValue) {
      return (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', cursor: 'pointer' }}
          onClick={openTimesPopup}
        >
          <Typography>-</Typography>
        </Stack>
      )
    }

    if (value) {
      return (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', cursor: 'pointer' }}
          onClick={openTimesPopup}
        >
          <Typography fontWeight="bold">
            {moment(value).format('HH:mm')}
          </Typography>
          <Typography variant="caption">
            {moment(value).format('Do MMM')}
          </Typography>
        </Stack>
      )
    }

    return (
      <Stack justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
        <IconButton
          disabled={updating || !canDispatch}
          onClick={handleUpdateTime}
        >
          <UpdateIcon />
        </IconButton>
      </Stack>
    )
  },
)

TimeCell.displayName = 'TimeCell'

const AssignedResources = ({ data }) => {
  const {
    selectedIncident,
    setSelectedIncident,
    selectedControlRoom,
    canDispatch = true,
  } = useContext(ControlRoomContext)

  const [openAssignResource, setOpenAssignResource] = useState(false)
  const [openResourceTimes, setOpenResourceTimes] = useState(false)
  const [updatingTimeKey, setUpdatingTimeKey] = useState(null)

  const assignedResourceRows = useMemo(
    () => selectedIncident?.assignedResources ?? [],
    [selectedIncident?.assignedResources],
  )

  const renderTimeCell = useCallback(
    (params, field, newStatus) => (
      <TimeCell
        row={params.row}
        value={params.value}
        field={field}
        newStatus={newStatus}
        selectedIncident={selectedIncident}
        selectedControlRoom={selectedControlRoom}
        setSelectedIncident={setSelectedIncident}
        setOpenResourceTimes={setOpenResourceTimes}
        updatingTimeKey={updatingTimeKey}
        setUpdatingTimeKey={setUpdatingTimeKey}
        canDispatch={canDispatch}
      />
    ),
    [
      selectedIncident,
      selectedControlRoom,
      setSelectedIncident,
      updatingTimeKey,
      canDispatch,
    ],
  )

  const columns = useMemo(
    () => [
      {
        field: 'callSign',
        headerName: 'Call Sign',
        flex: 1,
        minWidth: 120,
        valueGetter: (value, row) =>
          row?.callSign ?? row?.resource?.callSign ?? '',
      },
      {
        field: 'assigned',
        headerName: 'Assigned',
        headerAlign: 'center',
        align: 'center',
        minWidth: 110,
        valueGetter: (value, row) => row.times?.assigned,
        renderCell: (params) => renderTimeCell(params, 'assigned', 'Assigned'),
      },
      {
        field: 'mobile',
        headerName: 'Mobile',
        headerAlign: 'center',
        align: 'center',
        minWidth: 110,
        valueGetter: (value, row) => row.times?.mobile,
        renderCell: (params) => renderTimeCell(params, 'mobile', 'On Route'),
      },
      {
        field: 'arriveScene',
        headerName: 'Arrive Scene',
        headerAlign: 'center',
        align: 'center',
        minWidth: 120,
        valueGetter: (value, row) => row.times?.arriveScene,
        renderCell: (params) =>
          renderTimeCell(params, 'arriveScene', 'On Scene'),
      },
      {
        field: 'leaveScene',
        headerName: 'Leave Scene',
        headerAlign: 'center',
        align: 'center',
        minWidth: 120,
        valueGetter: (value, row) => row.times?.leaveScene,
        renderCell: (params) =>
          renderTimeCell(params, 'leaveScene', 'Leave Scene'),
      },
      {
        field: 'arriveHospital',
        headerName: 'Arrive Hospital',
        headerAlign: 'center',
        align: 'center',
        minWidth: 140,
        valueGetter: (value, row) => row.times?.arriveHospital,
        renderCell: (params) =>
          renderTimeCell(params, 'arriveHospital', 'At Hosp'),
      },
      {
        field: 'patientHandover',
        headerName: 'Patient Handover',
        headerAlign: 'center',
        align: 'center',
        minWidth: 150,
        valueGetter: (value, row) => row.times?.patientHandover,
        renderCell: (params) =>
          renderTimeCell(params, 'patientHandover', 'Handover'),
      },
      {
        field: 'clear',
        headerName: 'Clear',
        headerAlign: 'center',
        align: 'center',
        minWidth: 110,
        valueGetter: (value, row) => row.times?.clear,
        renderCell: (params) => renderTimeCell(params, 'clear', 'Available'),
      },
    ],
    [renderTimeCell],
  )

  return (
    <Grid item xs={12}>
      <Box component={Paper} padding={2} elevation={2}>
        <Grid container spacing={1}>
          <Grid item xs={12} lg={9}>
            <Typography variant="title2">Assigned Resources</Typography>
          </Grid>

          <Grid item xs={12} lg={3}>
            {canDispatch && (
              <Button
                onClick={() => setOpenAssignResource(true)}
                variant="contained"
                size="small"
                fullWidth
              >
                Assign Resource
              </Button>
            )}

            <AssignResource
              open={openAssignResource}
              onClose={() => setOpenAssignResource(false)}
              controlRoomID={selectedControlRoom}
              incident={selectedIncident}
              data={data}
              setSelectedIncident={setSelectedIncident}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomDataGrid
              rows={assignedResourceRows}
              columns={columns}
              getRowId={(row) => row?._id}
              slots={{
                toolbar: () => null,
                footer: () => null,
              }}
              disableRowSelectionOnClick
            />

            <BigModal
              open={!!openResourceTimes}
              onClose={() => setOpenResourceTimes(false)}
            >
              {openResourceTimes && (
                <ResourceTimesPopup
                  callSign={openResourceTimes?.callSign}
                  originalTimes={openResourceTimes?.times ?? {}}
                  assignedResourceRowID={openResourceTimes?._id}
                  resourceID={openResourceTimes?.resourceId}
                  onClose={() => setOpenResourceTimes(false)}
                />
              )}
            </BigModal>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  )
}

export default memo(AssignedResources)
